/* Two-way project bridge with the Atelier studio cockpit (studiogy.design).
   - Atelier → here: a project arrives as a ProjectBrief on ?atelier=… and opens
     as a draft contract.
   - here → Atelier: hand the current project over to Atelier, which creates a
     project (client portal, brief, stages…) from it.
   URL-param handoff (base64 JSON) so it works in embedded + real browsers. */

import type { ProjectBrief } from "../contracts/types";

const ATELIER_URLS = {
  prod: "https://studiogy.design",
  alt: "https://atelier.renauto.workers.dev",
  dev: "http://localhost:4341",
};
export function atelierUrl(): string {
  if (typeof location !== "undefined" && location.hostname === "localhost") return ATELIER_URLS.dev;
  return ATELIER_URLS.prod;
}

function b64encode(obj: unknown): string { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); }
function b64decode<T = any>(s: string): T | null {
  try { return JSON.parse(decodeURIComponent(escape(atob(s)))) as T; } catch { return null; }
}

/** Inbound: an Atelier project handed here as a ProjectBrief (?atelier=…). */
export function readInboundAtelier(search: string): ProjectBrief | null {
  const raw = new URLSearchParams(search).get("atelier");
  if (!raw) return null;
  const b = b64decode<any>(raw);
  if (!b || !b.projectName) return null;
  return {
    ...b,
    standard: b.standard || "AIA_B101_2017",
    projectName: b.projectName || "",
    projectLocation: b.projectLocation || "",
    worksDescription: b.worksDescription || "",
    employer: b.employer || { name: "", address: "" },
    contractor: b.contractor || { name: "", address: "" },
    contractPrice: b.contractPrice || { amount: 0, currency: "USD" },
    timeForCompletion: b.timeForCompletion || "",
    defectsNotification: b.defectsNotification || "12 months",
    governingLaw: b.governingLaw || "",
    rulingLanguage: b.rulingLanguage || "English",
    disputeResolution: b.disputeResolution || "",
    narrative: Array.isArray(b.narrative) ? b.narrative : [],
    specialProvisions: Array.isArray(b.specialProvisions) ? b.specialProvisions : [],
    drawings: Array.isArray(b.drawings) ? b.drawings : [],
  } as ProjectBrief;
}

/** Outbound: hand this project over to Atelier to create a project there. */
export function sendProjectToAtelier(b: ProjectBrief) {
  const payload = {
    name: b.projectName,
    type: "Private Residence",
    location: b.projectLocation || "",
    area: "",
    budget: b.contractPrice?.amount ?? b.estimatedConstructionCost?.amount ?? 0,
    currency: b.contractPrice?.currency ?? "USD",
    clientName: b.employer?.representative || b.employer?.name || "",
    clientCompany: b.employer?.name || "",
    clientEmail: b.employer?.email || "",
    briefSections: (b.narrative || []).map((n) => ({ title: n.heading, body: n.text })),
  };
  window.open(`${atelierUrl()}/import?data=${encodeURIComponent(b64encode(payload))}`, "_blank", "noopener");
}
